import { useCallback, useEffect, useRef } from 'react';

/**
 * Mémorise la dernière sélection texte non vide pour exécuter des commandes
 * depuis le bubble menu (la sélection est souvent perdue au clic sur un outil).
 */
export function useSavedEditorSelection(editor) {
  const savedRangeRef = useRef(null);

  useEffect(() => {
    if (!editor) return undefined;

    const remember = () => {
      const { from, to, empty } = editor.state.selection;
      if (!empty && from !== to) {
        savedRangeRef.current = { from, to };
      }
    };

    remember();
    editor.on('selectionUpdate', remember);
    editor.on('focus', remember);
    return () => {
      editor.off('selectionUpdate', remember);
      editor.off('focus', remember);
    };
  }, [editor]);

  const rememberNow = useCallback(() => {
    if (!editor || editor.isDestroyed) return;
    const { from, to, empty } = editor.state.selection;
    if (!empty && from !== to) {
      savedRangeRef.current = { from, to };
    }
  }, [editor]);

  const runWithSelection = useCallback((runner, { focus = true } = {}) => {
    if (!editor || editor.isDestroyed) return false;
    const range = savedRangeRef.current;
    let chain = focus ? editor.chain().focus() : editor.chain();
    if (range) {
      const docSize = editor.state.doc.content.size;
      const from = Math.max(0, Math.min(range.from, docSize));
      const to = Math.max(from, Math.min(range.to, docSize));
      chain = chain.setTextSelection({ from, to });
    }
    return runner(chain).run();
  }, [editor]);

  return { rememberNow, runWithSelection };
}
